import { Injectable } from '@angular/core';
import { ActionBackOfficeResponse } from '../../models/api.model';
import {
  EvBillingMode,
  EvConnectorTypeOption,
  EvConnectorTypeOptionDto,
  EvPriceRateRow,
  EvPriceRateRowDto,
  EvPriceRateTableDto,
  InsertEvPriceRatePayload,
  UpdateEvPriceRatePayload,
} from '../../models/system-settings/ev-price-rate.model';

@Injectable({ providedIn: 'root' })
export class MapperEvPriceRateService {
  mapTableFromDto(dto: EvPriceRateTableDto): {
    items: EvPriceRateRow[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } {
    const raw = dto as EvPriceRateTableDto & Record<string, unknown>;
    const data = (dto.data ?? raw['Data'] ?? []) as EvPriceRateRowDto[];
    return {
      items: data.map((row) => this.mapRow(row)),
      total: dto.total ?? (raw['Total'] as number) ?? 0,
      page: dto.page ?? (raw['Page'] as number) ?? 1,
      pageSize: dto.pageSize ?? (raw['PageSize'] as number) ?? 20,
      totalPages: dto.totalPages ?? (raw['TotalPages'] as number) ?? 0,
    };
  }

  mapRow(dto: EvPriceRateRowDto): EvPriceRateRow {
    const raw = dto as EvPriceRateRowDto & Record<string, unknown>;
    return {
      id: dto.id ?? (raw['Id'] as number) ?? 0,
      name: String(dto.name ?? raw['Name'] ?? ''),
      siteId: dto.siteId ?? (raw['SiteId'] as number) ?? 0,
      siteName: String(dto.siteName ?? raw['SiteName'] ?? ''),
      evConnectorTypeId: dto.evConnectorTypeId ?? (raw['EvConnectorTypeId'] as number) ?? 0,
      connectorCode: String(dto.connectorCode ?? raw['ConnectorCode'] ?? ''),
      connectorName: String(dto.connectorName ?? raw['ConnectorName'] ?? ''),
      billingMode: String(dto.billingMode ?? raw['BillingMode'] ?? ''),
      unitPrice: Number(dto.unitPrice ?? raw['UnitPrice'] ?? 0),
      unitMinute: this.asNumber(dto.unitMinute ?? raw['UnitMinute']),
      unitKwh: this.asNumber(dto.unitKwh ?? raw['UnitKwh']),
      gateId: this.asNumber(dto.gateId ?? raw['GateId']),
      gateName: String(dto.gateName ?? raw['GateName'] ?? ''),
      active: !!(dto.active ?? raw['Active'] ?? false),
      sortOrder: this.asNumber(dto.sortOrder ?? raw['SortOrder']),
      remark: String(dto.remark ?? raw['Remark'] ?? ''),
    };
  }

  mapConnector(dto: EvConnectorTypeOptionDto): EvConnectorTypeOption {
    const raw = dto as EvConnectorTypeOptionDto & Record<string, unknown>;
    return {
      id: dto.id ?? (raw['Id'] as number) ?? 0,
      code: String(dto.code ?? raw['Code'] ?? '').toLowerCase(),
      name: String(dto.name ?? raw['Name'] ?? ''),
      nameEn: String(dto.nameEn ?? raw['NameEn'] ?? ''),
      sortOrder: this.asNumber(dto.sortOrder ?? raw['SortOrder']),
    };
  }

  createEmpty(siteId: number, siteName = ''): EvPriceRateRow {
    return {
      id: 0,
      name: '',
      siteId,
      siteName,
      evConnectorTypeId: 0,
      connectorCode: '',
      connectorName: '',
      billingMode: 'time',
      unitPrice: 0,
      unitMinute: 60,
      unitKwh: null,
      gateId: null,
      gateName: '',
      active: true,
      sortOrder: 0,
      remark: '',
    };
  }

  mapInsertPayload(form: EvPriceRateRow, userId: number, menuId: number): InsertEvPriceRatePayload {
    return {
      userId,
      menuId,
      name: form.name?.trim() || null,
      siteId: form.siteId,
      evConnectorTypeId: form.evConnectorTypeId,
      billingMode: String(form.billingMode || '').toLowerCase(),
      unitPrice: Number(form.unitPrice) || 0,
      unitMinute: form.billingMode === 'time' ? form.unitMinute : null,
      unitKwh: form.billingMode === 'energy' ? form.unitKwh : null,
      gateId: form.gateId && form.gateId > 0 ? form.gateId : null,
      active: !!form.active,
      sortOrder: form.sortOrder,
      remark: form.remark?.trim() || null,
    };
  }

  mapUpdatePayload(form: EvPriceRateRow, userId: number, menuId: number): UpdateEvPriceRatePayload {
    return {
      ...this.mapInsertPayload(form, userId, menuId),
      id: form.id,
    };
  }

  mapActionResponse(res: ActionBackOfficeResponse | Record<string, unknown>) {
    const raw = res as Record<string, unknown>;
    return {
      isSuccess: !!((res as ActionBackOfficeResponse).isSuccess ?? raw['IsSuccess']),
      message: String((res as ActionBackOfficeResponse).message ?? raw['Message'] ?? ''),
    };
  }

  billingLabel(mode: string): string {
    switch ((mode || '').toLowerCase()) {
      case 'time':
        return 'เวลา (นาที)';
      case 'energy':
        return 'พลังงาน (kWh)';
      default:
        return mode || '—';
    }
  }

  unitDisplay(row: EvPriceRateRow): string {
    const mode = String(row.billingMode || '').toLowerCase() as EvBillingMode | string;
    if (mode === 'time') {
      return row.unitMinute != null ? `${row.unitMinute} นาที` : '—';
    }
    if (mode === 'energy') {
      return row.unitKwh != null ? `${row.unitKwh} kWh` : '—';
    }
    return '—';
  }

  applyConnectorDefaults(form: EvPriceRateRow, code: string): void {
    const c = (code || '').toLowerCase();
    if (c === 'ac') {
      form.billingMode = 'time';
      if (!form.unitMinute || form.unitMinute <= 0) form.unitMinute = 60;
      form.unitKwh = null;
    } else if (c === 'dc') {
      form.billingMode = 'energy';
      if (!form.unitKwh || form.unitKwh <= 0) form.unitKwh = 1;
      form.unitMinute = null;
    }
  }

  private asNumber(value: unknown): number | null {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
}
